// src/modules/app-views/products/pages/Document/tabs/RecordTab/ProductCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Col, Row, Form } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProductCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');
  const storedModule = useZustand('module', 'config');
  const { _id: documentId } = useDocumentContext() || {};
  const { form } = useSimpleFormContext() || {};

  // Watch current unitUid value to show removed unit if product is using it
  const currentUnitUid = Form.useWatch('unitUid', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const unitsOptions = useMemo(
    () => buildUnitsOptions(storedParameters, storedModule?.id, documentId, currentUnitUid),
    [storedParameters, storedModule?.id, documentId, currentUnitUid]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card title={<div className="text-muted">{translateX('product')}</div>}>
      {/* SKU, NAME, UNIT, PRICE */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={6} lg={5}>
          <SimpleInput name="sku" tooltip="auto_generated" />
        </Col>

        <Col xs={24} sm={18} lg={9}>
          <SimpleInput name="name" />
        </Col>

        <Col xs={24} sm={6} lg={4}>
          <SimpleSelect
            name="unitUid"
            label="unit"
            required
            allowClear
            showSearch
            options={unitsOptions}
            showAddButton={{ modal: 'parameters', tab: 'units' }}
          />
        </Col>

        <Col xs={24} sm={8} lg={6}>
          <SimpleCurrency
            name="price"
            currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
          />
        </Col>
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProductCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildUnitsOptions(storedParameters, moduleId, documentId, currentUnitUid) {
  const unitsArray = storedParameters?.modules?.[moduleId]?.units;

  if (!unitsArray || !Array.isArray(unitsArray)) return [];

  // Filter units:
  // - Show all active units (removed !== true)
  // - Show removed units ONLY if the current product is using it
  const isNewDocument = !documentId || documentId === 'add';

  const filteredUnits = unitsArray.filter(item => {
    if (!item.removed) return true;
    if (isNewDocument) return false;
    return item.uid === currentUnitUid;
  });

  return filteredUnits.map(item => {
    const name = item.name || '';
    const description = item.description || '';
    const displayLabel = description ? `${name} - ${description}` : name;

    return {
      value: item.uid,
      text: displayLabel,
      label: name,
      disabled: false,
    };
  });
}
